import { z } from 'zod';

/**
 * Form Validation Schemas
 * 
 * Centralized validation for all form types
 */

// Base contact fields (shared)
const baseContactSchema = {
  fullName: z.string().trim().min(2, 'Name must be at least 2 characters').max(100, 'Name must be less than 100 characters'),
  workEmail: z.string().trim().email('Please enter a valid email address').max(255, 'Email must be less than 255 characters'),
};

// Base contact fields with company name (for forms that need it)
const baseContactWithCompanySchema = {
  ...baseContactSchema,
  companyName: z.string().trim().min(2, 'Company name must be at least 2 characters').max(100, 'Company name must be less than 100 characters'),
};

// Phone validation
const phoneSchema = {
  countryCode: z.string().min(1, 'Please select a country code'),
  phoneNumber: z.string().trim().min(5, 'Please enter a valid phone number').max(20, 'Phone number is too long').regex(/^[\d\s-]+$/, 'Phone number can only contain digits, spaces, and dashes'),
};

// Primary Consultation Form Schema
export const consultationFormSchema = z.object({
  ...baseContactSchema,
  ...phoneSchema,
  service: z.string().min(1, 'Please select a service'),
  message: z.string().max(1000, 'Message must be less than 1000 characters').optional(),
});

export type ConsultationFormData = z.infer<typeof consultationFormSchema>;

// Project Discovery Form Schema
export const discoveryFormSchema = z.object({
  ...baseContactWithCompanySchema,
  websiteUrl: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  role: z.string().min(1, 'Please select your role'),
  service: z.string().min(1, 'Please select a service'),
  budgetRange: z.string().min(1, 'Please select a budget range'),
  timeline: z.string().min(1, 'Please select a timeline'),
  challenges: z.array(z.string()).min(1, 'Please select at least one challenge'),
  message: z.string().max(1000, 'Message must be less than 1000 characters').optional(),
});

export type DiscoveryFormData = z.infer<typeof discoveryFormSchema>;

// Analytics/Audit Form Schema
export const auditFormSchema = z.object({
  ...baseContactWithCompanySchema,
  websiteUrl: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  currentTools: z.array(z.string()).min(1, 'Please select at least one tool'),
  trackingConfidence: z.number().min(1).max(5),
  auditFocus: z.string().min(1, 'Please select an audit focus area'),
  message: z.string().max(1000, 'Message must be less than 1000 characters').optional(),
});

export type AuditFormData = z.infer<typeof auditFormSchema>;

// Careers Form Schema
export const careersFormSchema = z.object({
  fullName: z.string().trim().min(2, 'Name must be at least 2 characters').max(100, 'Name must be less than 100 characters'),
  email: z.string().trim().email('Please enter a valid email address').max(255, 'Email must be less than 255 characters'),
  roleApplying: z.string().min(1, 'Please select a role'),
  linkedinUrl: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  coverLetter: z.string().max(2000, 'Cover letter must be less than 2000 characters').optional(),
});

export type CareersFormData = z.infer<typeof careersFormSchema>;

// Partnership Form Schema
export const partnershipFormSchema = z.object({
  companyName: z.string().trim().min(2, 'Company name must be at least 2 characters').max(100, 'Company name must be less than 100 characters'),
  contactName: z.string().trim().min(2, 'Name must be at least 2 characters').max(100, 'Name must be less than 100 characters'),
  email: z.string().trim().email('Please enter a valid email address').max(255, 'Email must be less than 255 characters'),
  partnershipType: z.string().min(1, 'Please select a partnership type'),
  collaborationIdea: z.string().trim().min(10, 'Please describe your collaboration idea').max(1000, 'Description must be less than 1000 characters'),
});

export type PartnershipFormData = z.infer<typeof partnershipFormSchema>;
